import type { TokenCategory } from "@repo/shared-types";

import { Badge } from "../ui/Badge";
import { Card, CardContent } from "../ui/Card";

type TokenPreviewProps = {
	name?: string | undefined;
	symbol?: string | undefined;
	category?: TokenCategory | undefined;
	logoPreviewUrl?: string | null | undefined;
	tagline?: string | undefined;
};

// Playing card gradients per UX-4
const categoryGradients: Record<TokenCategory, string> = {
	AI: "bg-gradient-to-br from-blue-500 to-purple-600", // Diamonds
	DeFi: "bg-gradient-to-br from-gray-700 to-black", // Spades
	Gaming: "bg-gradient-to-br from-green-500 to-teal-600", // Clubs
	Creator: "bg-gradient-to-br from-red-500 to-pink-600", // Hearts
	Meme: "bg-gradient-to-br from-orange-400 to-yellow-500",
	Social: "bg-gradient-to-br from-purple-400 to-indigo-500",
	Infrastructure: "bg-gradient-to-br from-gray-500 to-slate-600",
	Other: "bg-gradient-to-br from-neutral-400 to-gray-500",
};

export function TokenPreview({
	name,
	symbol,
	category,
	logoPreviewUrl,
	tagline,
}: TokenPreviewProps) {
	return (
		<div className="sticky top-6">
			<h3 className="text-lg font-semibold text-pastel-black mb-4">Preview</h3>
			<Card className="overflow-hidden">
				<CardContent className="p-6">
					<div className="flex items-start gap-4">
						{/* Logo placeholder */}
						<div className="w-12 h-12 rounded-full bg-pastel-light-gray flex items-center justify-center flex-shrink-0 overflow-hidden">
							{logoPreviewUrl ? (
								<img
									src={logoPreviewUrl}
									alt="Token logo"
									className="w-full h-full object-cover"
									loading="lazy"
								/>
							) : (
								<div className="text-xs text-pastel-gray">?</div>
							)}
						</div>

						{/* Token info */}
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2 mb-1">
								<h4 className="font-semibold text-pastel-black truncate">{name || "Token Name"}</h4>
								<span className="text-sm text-pastel-gray">{symbol || "SYMBOL"}</span>
							</div>

							{tagline && <p className="text-sm text-pastel-gray mb-2 line-clamp-2">{tagline}</p>}

							{category && (
								<Badge className={`text-white ${categoryGradients[category]}`}>{category}</Badge>
							)}
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
